import {
  CSViewRealizerStructure,
  UpdateBase,
  MultiMessage,
  DataRequest,
  UserInfo,
  TNclInputType,
  DEFAULT_LANG_IMG,
  LoginInfo,
  OutData,
  CSClientData,
  clNone,
  ReconnectData,
  LoginData,
  AppData,
  CSUpdateCommandItem,
  getNotifySvg,
  GPSDataRequest,
} from "./common/communication.base";
import { Helper, Log, delay, parseData, isNewVersionAvailable, Version, GPSReader } from "./common/common";
import { Context } from "./appcontext";
import { ViewRealizer, ViewRealizerManager, RealizerOperations } from "./viewrealizer";
import { NclMessageType, NclMessage, NclMessageHelper, ReceiveData, Connection, PostbackMessage } from "./common/communication";
import { NclBaseTabControl, NclButton, NclCommandItem, NclControlBase, NclDockControl, NclTabControl } from "./common/components.ncl";
import { ModalPosition } from "./components/K2Modal";
import { CursorProperty } from "csstype";
import { initSmartlook } from "./smartlook";

interface NotifyIndicator {
  root: HTMLElement;
  indicator: HTMLElement;
}

interface LastMessage {
  received: NclMessage;
  sent: NclMessage & { control: NclControlBase };
}

/**
 * Základní třída celé aplikace.
 * Navazuje spojení se serverem pomoci websocket a zpracovává zprávy přijimané ze serveru.
 * Obsahuje seznam aktivních realieru kterým jsou zprávy předávané ke zpracování.
 */
export abstract class ApplicationBase {
  private isReconnecting: boolean;
  private reconnectData: ReconnectData;
  protected userInfo: UserInfo;
  private connection: Connection;
  private _appViewRealizer: ViewRealizer;
  private _screenSize: number;
  private _lastScreenSize: number;
  private ignore: number = 0; //promenna pro moznost zablokovani posilani pozadavku na server
  private menuPosition: ModalPosition;
  private modalRealizerUID: string;
  private busyIndicator: BusyIndicator = new BusyIndicator();
  private fullScreenMessage = new FullScreenMessage();
  private timer: any;
  private messagesToSend: Array<NclMessage>;
  private activeVRUID: string;
  private mainTabControlUID: string;
  private companyColor: number;
  private lastUserActivity: Date;
  private actionControlData: { actionControlUID: string; realizerUID: string };
  private activityEvents = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
  private modalCount: number = 0;
  private appData: AppData;
  private notifyIndicator: NotifyIndicator | string;
  private version: string;
  protected hideReloadAlert: boolean;
  private user: string;
  private lastMessage: LastMessage = { received: null, sent: null };
  protected lastRequestStartTime: number;
  private errorMessageToken: string;
  private gpsReader: GPSReader;

  constructor() {
    this._appViewRealizer = null;
    this.isReconnecting = false;
    this.messagesToSend = new Array<NclMessage>();
  }

  public get ActionControlData(): { actionControlUID: string; realizerUID: string } {
    return this.actionControlData;
  }

  public get BusyIndicator(): BusyIndicator {
    return this.busyIndicator;
  }

  public set ActionControlData(data: { actionControlUID: string; realizerUID: string }) {
    this.actionControlData = data;
  }

  public get ModalCount(): number {
    return this.modalCount;
  }

  public get Version(): string {
    return this.version;
  }

  public set Version(version: string) {
    this.version = version;
  }

  public get HideReloadAlert(): boolean {
    return this.hideReloadAlert;
  }

  public get User(): string {
    return this.user;
  }

  public get ErrorMessageToken() {
    return this.errorMessageToken;
  }

  public get ReconnectData(): ReconnectData {
    return this.reconnectData;
  }

  public get LastMessage(): LastMessage {
    return this.lastMessage;
  }

  public get UserInfo(): UserInfo {
    return this.userInfo;
  }

  public getGPSReader(): GPSReader {
    if (!this.gpsReader) {
      this.gpsReader = new GPSReader();
    }

    return this.gpsReader;
  }

  protected internalRender(): Promise<void> {
    window.addEventListener("keydown", this.keyDown);
    window.addEventListener("orientationchange", this.orientationchange);
    window.addEventListener("mousemove", this.mousemove);
    window.addEventListener("error", this.handleError);
    window.addEventListener("unhandledrejection", this.handleRejection);
    return null;
  }

  private startNoopTimeout() {
    if (this.userInfo && this.userInfo.NoopTimeout > 0) {
      if (this.userInfo.InactivityTimeout > 0) this.lastUserActivity = new Date();
      let interval = this.userInfo.NoopTimeout;
      if (this.userInfo.InactivityTimeout && interval > this.userInfo.InactivityTimeout) {
        interval = this.userInfo.InactivityTimeout;
      }
      interval = interval < 120 ? 60 : interval / 2;
      let __this = this;
      this.stopNoopTimeout();
      this.timer = setInterval(() => {
        const inactivitySec = __this.userInfo.InactivityTimeout > 0 ? (new Date().getTime() - __this.lastUserActivity.getTime()) / 1000 : undefined;
        if (__this.isReconnecting) return;
        if (
          (new Date().getTime() - __this.getConnection().LastActivity.getTime()) / 1000 >= __this.userInfo.NoopTimeout ||
          (inactivitySec !== undefined && inactivitySec >= __this.userInfo.InactivityTimeout)
        ) {
          __this.internalSendMessage(NclMessageHelper.CreateNoopMsg(inactivitySec));
        }
      }, interval * 1000);
    }
  }

  private stopNoopTimeout() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  getCompanyColor(): number {
    return this.companyColor;
  }

  setCompanyColor(value: number) {
    if (value === clNone) this.companyColor = 0;
    else this.companyColor = value;
  }

  public getMainDockControl(): NclDockControl {
    return null;
  }

  public getActiveRealizer(): ViewRealizer {
    let result: ViewRealizer;
    if (this.activeVRUID) {
      result = ViewRealizerManager.getViewRealizer(this.activeVRUID);
    }

    if (!result) {
      return this.appViewRealizer;
    }

    return result;
  }

  public setActiveRealizerUID(realizer: ViewRealizer) {
    if (realizer && realizer.isActive() && this.activeVRUID !== realizer.getRealizerUID()) {
      let vr = ViewRealizerManager.getViewRealizer(this.activeVRUID);
      if (vr) {
        vr.notifyAsActive(false);
      }
      this.activeVRUID = realizer.getRealizerUID();
      realizer.notifyAsActive(true);
    }
  }

  public registerMainTabControl(ctrl: NclBaseTabControl<any, any>): boolean {
    if (!this.mainTabControlUID) {
      this.mainTabControlUID = ctrl.MetaData.ControlUID;
      return true;
    }
    return false;
  }

  public unRegisterMainTabControl(ctrl: NclBaseTabControl<any, any>) {
    if (this.mainTabControlUID === ctrl.MetaData.ControlUID) {
      this.mainTabControlUID = undefined;
    }
  }

  public get appViewRealizer(): ViewRealizer {
    return this._appViewRealizer;
  }

  private async reconnect(lastmessage: NclMessage) {
    if (this.reconnectData && this.reconnectData.ReconnectUID) {
      if (!this.isReconnecting) {
        this.isReconnecting = true;
        this.busyIndicator.setBusyIndicator(true, "Prosím čekejte...", `Pokus o připojení`, "wait", true);
        this.stopNoopTimeout();
        await delay(500);

        this.connection = null;
        this.getConnection()
          .connect()
          .then(async () => {
            if (Context.DeviceInfo.AutoLogin === "1") {
              const version = await isNewVersionAvailable();

              if (version === Version.new) {
                this.reloadPage();
              }
            }

            this.close(true, false);
            this.ignore = 0;
            this.internalSendMessage(NclMessageHelper.CreateRealizeAppMsg(this.reconnectData), (reason) => {
              this.busyIndicator.setBusyIndicator(false);
              this.isReconnecting = false;
              if (reason === true) {
                if (lastmessage) {
                  this.sendMessage(lastmessage);
                }
                this.startNoopTimeout();
              } else {
                location.reload();
              }
            });
          })
          .catch((reason) => {
            this.busyIndicator.setBusyIndicator(false);
            return this.reconnect(lastmessage);
          });
      } else {
        this.reconnectForm(async () => {
          this.isReconnecting = false;
          await this.reconnect(lastmessage);
        });
      }
    } else {
      location.reload();
    }
  }

  private tSep: string = undefined;
  private dSep: string = undefined;
  private nSep: string = undefined;
  public getLocalSeparator(type: TNclInputType): string {
    switch (type) {
      case TNclInputType.nitNumeric:
        if (!this.nSep) {
          let n = 1.1;
          this.nSep = n.toLocaleString().substring(1, 2);
        }
        return this.nSep;
      case TNclInputType.nitDate:
        if (!this.dSep) {
          let date = new Date();
          let dateStr = date.toLocaleDateString();
          let dayStr = date.getDate().toString();
          let sepNdx = dateStr.indexOf(dayStr) + dayStr.length;
          this.dSep = dateStr.substring(sepNdx, sepNdx + 1).trim();
        }

        return this.dSep;
      case TNclInputType.nitTime:
        if (!this.tSep) {
          let date = new Date();
          let dateStr = date.toLocaleTimeString();
          let hourStr = date.getHours().toString();
          let hourNdx = dateStr.indexOf(hourStr) + hourStr.length;
          this.tSep = dateStr.substring(hourNdx, hourNdx + 1).trim();
        }
        return this.tSep;
      default:
        break;
    }
    return "";
  }

  private clear() {
    this.modalRealizerUID = null;
    this._appViewRealizer = null;
    ViewRealizerManager.clear();
    this.internalClear();
  }

  protected abstract internalClear(): void;

  public render(vr: ViewRealizer) {
    this._appViewRealizer = vr;

    if (this.userInfo && this.userInfo.InactivityTimeout > 0) {
      const __this = this;
      this.activityEvents.forEach(function (eventName) {
        document.addEventListener(eventName, __this.userActivityHandler, true);
      });
    }
    this.internalRender();
  }

  public getPictureServerUrl(isStatic: boolean): string {
    return isStatic ? `${this.getConnection().getBaseUrl()}/images` : `${this.getConnection().getBaseUrl()}/Picture/GetPicture?`;
  }

  public getCurrentK2LanguageImgFolder(): string {
    if (!this.userInfo) return DEFAULT_LANG_IMG;
    else return `Lang/Lang${this.userInfo.Lng}`;
  }

  public addActualScreenSize(request: DataRequest) {
    if (this._screenSize !== this._lastScreenSize) {
      request.ScreenSize = this._screenSize;
      request.TransformColumnsCount = Context.DeviceInfo.TransformColumnsCount;
      this._lastScreenSize = this._screenSize;
    }
  }

  public getUserLanguage(): string {
    let userLang = navigator.language;
    if (userLang && userLang != "undefined") {
      return userLang;
    }
    return "en-US";
  }

  /**
   * Spuštění aplikace.
   */
  public async run() {
    this.loginForm();
  }

  public showModal(realizerUID: string): Promise<void> {
    if (this.appViewRealizer) {
      this.modalCount++;
      this.modalRealizerUID = realizerUID;
      return ViewRealizer.getNearestListener(this.appViewRealizer).showModal(realizerUID);
    }
    return Promise.reject("App realizer not found.");
  }

  public closeModal(realizerUID: string): Promise<void> {
    if (this.appViewRealizer) {
      this.modalCount--;
      this.modalRealizerUID = null;
      return ViewRealizer.getNearestListener(this.appViewRealizer).closeModal(realizerUID);
    }

    return Promise.reject("App realizer not found.");
  }

  protected abstract loginForm(): void;

  protected abstract reconnectForm(reconnectFce: () => void): void;

  public realizeApp(data: LoginData, username: string) {
    this.user = username;
    this.errorMessageToken = data.ErrorMessageToken;

    initSmartlook(data.Settings?.SmartlookProjectKey, data.Settings?.EnableSmartlookForUsers, username);

    if (this.appViewRealizer === null) {
      if (data) {
        this.reconnectData = Object.assign({ ReconnectUID: "" }, data) as ReconnectData;
      }
      this.internalSendMessage(NclMessageHelper.CreateRealizeAppMsg(undefined));
    }
  }

  public async login(user: string, password: string): Promise<LoginData> {
    return this.getConnection().login(user, password);
  }

  public async getLoginInfo(user: string): Promise<LoginInfo> {
    return this.getConnection().getLoginInfo(user);
  }

  /**
   * Ukončení aplikace.
   */
  public async close(safeReconnect: boolean, sendTerminate: boolean) {
    if (!this._appViewRealizer) return;
    if (sendTerminate) {
      navigator.sendBeacon(
        `${this.connection.getBaseUrl()}/close`,
        JSON.stringify(NclMessageHelper.CreateTerminateMsg(this._appViewRealizer ? this._appViewRealizer.getRealizerUID() : ""))
      );
    }

    if (safeReconnect === false) this.reconnectData = null;
    window.removeEventListener("keydown", this.keyDown);
    window.removeEventListener("orientationchange", this.orientationchange);
    window.removeEventListener("mousemove", this.mousemove);
    window.removeEventListener("error", this.handleError);
    window.removeEventListener("unhandledrejection", this.handleRejection);

    if (this.userInfo && this.userInfo.InactivityTimeout > 0) {
      const __this = this;
      this.activityEvents.forEach(function (eventName) {
        document.removeEventListener(eventName, __this.userActivityHandler, true);
      });
    }

    this.clear();
  }

  /**
   * Methods forbid send messages to server.
   */
  public forbidenSendMessages() {
    this.ignore++;
  }

  /**
   * Methods allow send messages to server and send message from queue.
   */
  public allowSendMessages() {
    if (this.ignore > 0) this.ignore--;

    if (this.ignore === 0) {
      if (this.messagesToSend.length > 0) {
        let msg = this.messagesToSend.shift();
        if (msg) {
          if (this.sendMessage(msg) !== true) {
            this.allowSendMessages();
          }
        }
      }
    }
  }

  private userActivityHandler() {
    const __this = Context.getApplication();
    __this.lastUserActivity = new Date();
  }

  private canSendMessage(msg: NclMessage): boolean {
    return !this.modalRealizerUID || this.modalRealizerUID === msg.realizerUID || msg.messageType == NclMessageType.nmsgCheckUnexpected;
  }

  public isBusy(): boolean {
    return this.ignore > 0 || this.isReconnecting === true;
  }

  public reloadPage() {
    this.hideReloadAlert = true;
    location.reload();
  }

  public sendMessage(message: NclMessage, control?: NclControlBase): boolean {
    if (!this.canSendMessage(message)) return false;
    if (!this.isBusy()) {
      let vr = ViewRealizerManager.getViewRealizer(message.realizerUID);
      if (message.realizerUID && (!vr || !vr.canSendRequest())) return false;

      if (message.messageType !== NclMessageType.nmsgCheckUnexpected) {
        this.lastMessage.sent = {
          ...message,
          control: control,
        };
      }

      this.internalSendMessage(message);
      return true;
    }

    this.messagesToSend.push(message);
    return true;
  }

  private internalSendMessage(message: NclMessage, onReconnectReceive: (reason: boolean) => void = null) {
    this.forbidenSendMessages();
    this.lastRequestStartTime = new Date().getTime();
    if (!this.getConnection().send(message)) {
      this.allowSendMessages();
      this.reconnect(message);
      return;
    }
    if (message.messageType !== NclMessageType.nmsgNoop) this.stopNoopTimeout();

    this.busyIndicator.setBusyIndicator(true);

    //console.time('sendMessage');
    this.getConnection()
      .receive()
      .then(async (result: ReceiveData) => {
        //console.timeEnd('sendMessage');
        Log.debug(
          "REQUEST:" +
            JSON.stringify(message) +
            " RESPONSE: " +
            JSON.stringify(result) +
            " - DURATION: " +
            (new Date().getTime() - this.lastRequestStartTime) +
            "ms"
        );
        this.busyIndicator.setBusyIndicator(false);
        if (message.messageType !== NclMessageType.nmsgNoop) this.startNoopTimeout();
        if (result) {
          if (result.message && result.message.messageType === NclMessageType.nmsgTerminate) {
            if (onReconnectReceive !== null) {
              onReconnectReceive(false); //reconnect session on server was killed
            } else {
              this.terminate(result.message);
            }
            return;
          }
          if (message.messageType === NclMessageType.nmsgNoop) {
            if (this.reconnectData && result.message.realizerUID) {
              this.reconnectData.ReconnectUID = result.message.realizerUID;
            }
            this.allowSendMessages();
            return;
          }

          await NclMessageHelper.unzipMessage(result.message);
          await this.receiveNclMessage(result.message);
          this.allowSendMessages();
          this.executeModalMenuAction();

          if (result.error) {
            Log.error("Send message: " + message + "response: " + result.error.message, result.error);
            onReconnectReceive(false); // error reconnect
          } else {
            if (onReconnectReceive) onReconnectReceive(true); // reconnect success
          }
        } else {
          Log.error("Send message: " + message + "response: null.", null);
          this.allowSendMessages();
        }
      })
      .catch((reason: any) => {
        Log.error(reason, null);
        this.busyIndicator.setBusyIndicator(false);
        if (reason.code === 1000) {
          if (onReconnectReceive) onReconnectReceive(false);
        } else {
          this.allowSendMessages();
          this.reconnect(message);
        }
      });
  }

  public executeModalMenuAction() {
    if (this.ActionControlData) {
      (ViewRealizerManager.getViewRealizer(this.actionControlData.realizerUID).getControlByUID(
        this.ActionControlData.actionControlUID
      ) as NclCommandItem).executeCommand();
      this.ActionControlData = null;
    }
  }

  public canStopPostback(message: PostbackMessage): boolean {
    if (this.busyIndicator) {
      this.busyIndicator.setPostbackProgress(message.ProgressTitle, message.ProgressPercentage);
    }
    return this.busyIndicator.canStopPostback();
  }

  public setMenuPosition(x: number, y: number) {
    if (x && y) {
      this.menuPosition = { x: x, y: y };
    }
  }

  private receiveNclMessage(data: NclMessage): Promise<void | void[]> {
    this.lastMessage.received = data;

    if (data) {
      if (data.messageType == NclMessageType.nmsgMulti) {
        let messages: MultiMessage[] = parseData(data.json);
        if (messages != null && messages.length > 0) {
          return new Promise((resolve, reject) => {
            this.next(messages, 0, resolve);
          });
        }
      } else {
        return new Promise((resolve, reject) => {
          this.processMessage(data, resolve);
        });
      }
    }
  }

  private next(messages: MultiMessage[], index: number, callback: () => void) {
    if (messages && messages.length > 0 && index < messages.length) {
      this.processMessage(NclMessageHelper.Create(messages[index]), () => {
        return this.next(messages, index + 1, callback);
      });
    } else {
      callback();
    }
  }

  private async processMessage(message: NclMessage, callback: () => void) {
    switch (message.messageType) {
      case NclMessageType.nmsgRealize:
        let structure: CSViewRealizerStructure = parseData(message.json);
        if (this.appViewRealizer && structure.RealizerUID === this.appViewRealizer.getRealizerUID()) {
          this.close(true, false);
        }
        let vr = ViewRealizerManager.getOrCreate(message.realizeCounter, structure);
        if (vr != null) {
          vr.processData(message.realizeCounter, structure, callback);
          return;
        }
        break;
      case NclMessageType.nmsgData:
        {
          let vr = ViewRealizerManager.getViewRealizer(message.realizerUID);
          if (vr != null) {
            let data: UpdateBase = parseData(message.json);
            vr.processData(message.realizeCounter, data, callback);
            return;
          }
        }
        break;
      case NclMessageType.nmsgClose:
        {
          let vr = ViewRealizerManager.getViewRealizer(message.realizerUID);
          if (vr != null) {
            await vr.close();
            //reamove all request for closing VR.
            this.messagesToSend = this.messagesToSend.filter((msg) => {
              return msg.realizerUID !== message.realizerUID;
            });
            if (callback) callback();
            return;
          }
        }
        break;
      case NclMessageType.nmsgOutData: {
        let files: OutData = parseData(message.json);
        this.processClientData(files.OutData);
        break;
      }
      case NclMessageType.nmsgTerminate:
        this.terminate(message);
        break;
      case NclMessageType.nmsgOK:
      default:
        if (!this.appViewRealizer) {
          if (this.reconnectData) {
            this.reconnectData.ReconnectUID = message.realizerUID;
            if (message.json) {
              this.userInfo = parseData(message.json) as UserInfo;
              if (this.userInfo && this.userInfo.NoopTimeout > 0) {
                this.startNoopTimeout();
              }
            }
            this.sendMessage(NclMessageHelper.CreateRealizeMsg());
          }
        } else {
          if (message.realizerUID === this.appViewRealizer.getRealizerUID() && message.json) {
            let appData = JSON.parse(message.json);
            if (appData) {
              this.setAppData(appData);
            }
          }
        }
        break;
    }

    if (callback) {
      callback();
    }
  }
  private setAppData(appData: AppData) {
    if (!this.appData) {
      this.appData = {} as AppData;
    }
    let nc = this.appData.NotifyCount;
    Object.assign(this.appData, appData);
    if (this.appData.NotifyCount !== nc) {
      if (this.notifyIndicator) {
        if (typeof this.notifyIndicator == "string") {
          let root = document.getElementById(this.notifyIndicator);
          if (root) {
            let ni = { root: root } as NotifyIndicator;
            ni.indicator = document.getElementById(this.notifyIndicator + "_indicator");
            if (ni.indicator) this.notifyIndicator = ni;
          }
        }
        if (this.notifyIndicator && typeof this.notifyIndicator !== "string") {
          if (!this.notifyIndicator.indicator) {
            this.notifyIndicator = undefined;
          } else {
            if (this.appData.NotifyCount <= 0) {
              this.notifyIndicator.indicator.setAttribute("visibility", "hidden");
            } else {
              this.notifyIndicator.indicator.setAttribute("visibility", "visible");
            }
          }
        }
      }

      if (!this.notifyIndicator) {
        let ctrl = this.appViewRealizer.findControlByName("ShowNotification");
        if (ctrl instanceof NclCommandItem) {
          this.notifyIndicator = `notifyIndicator_${this.appViewRealizer.getRealizerUID()}`;
          ctrl.updateState({ GlyphId: `svg*${getNotifySvg(this.notifyIndicator, this.appData.NotifyCount.toString())}` } as CSUpdateCommandItem);
        }
      }
    }
  }

  public terminate(message?: NclMessage) {
    // console.log("Receive terminate");
    if (message && message.json) {
      console.log(message.json);
    } else {
      document.cookie = "login=";
    }

    this._appViewRealizer = null;
    this.stopNoopTimeout();
    location.reload();
  }

  protected abstract processClientData(list: Array<CSClientData>): void;

  private getConnection(): Connection {
    if (this.connection == null) {
      let url: string = window.location.host + window.location.pathname;
      if (url.endsWith("/")) {
        url = url.slice(0, -1);
      }

      if (process.env.NODE_ENV === "development") {
        //ws = 'ws://172.21.83.108:8080/ws';
        //url = 'labs.k2.cz:8082';
        // url = '172.21.83.135:8080';//moje PC
        //url = 'https://hetera.k2.cz/k2';
        //url = 'glivickyd-p677.k2.int/HTML5_k2_AT';
        url = "dziekanikm-p608.k2.int/web_2022";
        //url ='labs.k2.cz/gaia';
      }
      this.connection = new Connection(url, location.protocol == "https:");
    }

    return this.connection;
  }

  /**
   * Odchyt stisku tl. Vyvolá metodu keyDown na vsech zaregistrovaných realizerech a pokud ji nektery obslouží tak
   * končí a události zakáže probublání dále.
   */
  private keyDown = (ev: KeyboardEvent) => {
    let vr: ViewRealizer = Context.getApplication().getActiveRealizer();
    vr = vr == null ? this.appViewRealizer : vr;
    if (vr != null) {
      let hk = Helper.convertToTxt(ev);
      if (hk != "" && vr.processHotKey(hk)) {
        if (hk.includes("LEFT") || hk.includes("RIGHT")) return false;

        ev.preventDefault();
        ev.stopPropagation();
        return false;
      }
    }
  };

  private orientationchange = (event: Event) => {
    window.addEventListener("resize", this.handleResize);
  };

  handleResize = () => {
    let size = Context.DeviceInfo.ScreenWidth;
    if (size != this._lastScreenSize) {
      this._screenSize = size;
      Context.getApplication().getActiveRealizer().sendRequest(RealizerOperations.Update);
    }
    window.removeEventListener("resize", this.handleResize);
  };

  private mousemove(event: MouseEvent) {
    Context.getApplication().setMenuPosition(event.clientX, event.clientY);
  }

  private handleError(e: ErrorEvent) {
    if (e.error) {
      Helper.sendErrorMessage(e.message, e.error.stack);
    }
  }

  private handleRejection(e: PromiseRejectionEvent) {
    throw new Error(e.reason.stack);
  }

  public getMenuPositon(): ModalPosition {
    return this.menuPosition;
  }
}

export class BusyIndicator {
  private busyIndicatorStartTimer: any;
  private busyIndicatorStopTimer: any;
  private busyIndicatorCount: number = 0;
  private busyIndicatorStartFlag: boolean = false;
  private busyIndicatorStopFlag: boolean = false;
  private text: string = "";
  private cursor: CursorProperty = "";
  private oldCursor: CursorProperty;

  private busyIndicatorSmall: HTMLElement = document.getElementById("busyIndicatorSmall");
  private busyIndicatorBig: HTMLElement = document.getElementById("busyIndicatorBig");
  private busyIndicatorTextContainer: HTMLElement = document.getElementById("busyIndicatorTextContainer");
  private busyIndicatorContainer: HTMLElement = document.getElementById("busyIndicatorContainer");
  private busyIndicatorStopBtn: HTMLElement = document.getElementById("busyIndicatorStopBtn");
  private stopPostback: boolean;

  public canStopPostback(): boolean {
    return this.stopPostback;
  }

  public setPostbackProgress = (title: string, message: string): void => {
    if (this.busyIndicatorSmall && this.busyIndicatorBig && this.busyIndicatorStopBtn) {
      if (this.busyIndicatorCount > 0) {
        this.changeVisibilityBigIndicator(true, title, message);
        this.busyIndicatorStopBtn.setAttribute("style", "display: flex;");
        if (!this.busyIndicatorStopBtn.onclick) {
          this.busyIndicatorStopBtn.onclick = (e) => {
            this.stopPostback = true;
          };
          this.busyIndicatorStopBtn.onmouseenter = (e) => {
            document.body.style.cursor = "";
          };
          this.busyIndicatorStopBtn.onmouseleave = (e) => {
            document.body.style.cursor = this.cursor;
          };
        }
      }
    }
  };

  public setBusyIndicator = (
    showIndicator: boolean = true,
    title: string = "",
    message: string = "",
    cursor: CursorProperty = "wait",
    forceBigIndicator: boolean = false
  ): void => {
    this.cursor = cursor;
    if (this.busyIndicatorSmall && this.busyIndicatorBig) {
      if (showIndicator) {
        clearTimeout(this.busyIndicatorStopTimer);
        this.busyIndicatorStopFlag = false;

        this.busyIndicatorCount++;
        if (forceBigIndicator) {
          this.changeVisibilitySmallIndicator(true);
          this.changeVisibilityBigIndicator(true, title, message);
        } else if (this.busyIndicatorCount === 1) {
          this.changeVisibilitySmallIndicator(true);
          if (!this.busyIndicatorStartFlag) {
            this.busyIndicatorStartFlag = true;
            this.busyIndicatorStartTimer = setTimeout(() => {
              this.changeVisibilityBigIndicator(true, title, message);
            }, 750);
          }
        }
        if (message !== "" && this.text !== message) {
          this.changeVisibilityBigIndicator(true, title, message);
        }
      } else {
        this.busyIndicatorCount--;
        this.changeVisibilitySmallIndicator(false);
        this.busyIndicatorStopFlag = true;
        this.busyIndicatorStopTimer = setTimeout(() => {
          clearTimeout(this.busyIndicatorStartTimer);
          this.busyIndicatorStartFlag = false;
          this.changeVisibilityBigIndicator(false, "", "");
        }, 500);
      }
    }
  };
  private changeVisibilitySmallIndicator(visible: boolean) {
    if (visible) {
      this.setCursor();
      this.busyIndicatorSmall.setAttribute("style", "opacity: 1;z-index: 0;");
    } else if (this.busyIndicatorCount === 0) {
      document.body.style.cursor = this.oldCursor;
      this.oldCursor = null;
      this.busyIndicatorSmall.setAttribute("style", "opacity: 0;z-index: -1;");
    }
  }

  private changeVisibilityBigIndicator(visible: boolean, title: string, message: string) {
    if (visible) {
      this.setCursor();
      if (!this.busyIndicatorStopFlag) {
        this.busyIndicatorBig.setAttribute("style", "display: flex;");
        if (title != "" || message !== "") {
          this.busyIndicatorContainer.classList.add("showBusyText");

          this.busyIndicatorTextContainer.getElementsByClassName("busyIndicatorTitle")[0].innerHTML = title;
          this.busyIndicatorTextContainer.getElementsByClassName("busyIndicatorMessage")[0].innerHTML = message;
          this.text = message;
        }
      }
    } else {
      this.stopPostback = undefined;
      this.busyIndicatorContainer.classList.remove("showBusyText");
      this.busyIndicatorBig.setAttribute("style", "display: none;");
      this.busyIndicatorStopBtn && this.busyIndicatorStopBtn.setAttribute("style", "display: none;");
      this.busyIndicatorTextContainer.getElementsByClassName("busyIndicatorTitle")[0].innerHTML = "";
      this.busyIndicatorTextContainer.getElementsByClassName("busyIndicatorMessage")[0].innerHTML = "";
      document.body.style.cursor = this.oldCursor;
      this.oldCursor = null;
    }
  }

  private setCursor() {
    if (!this.oldCursor) this.oldCursor = document.body.style.cursor;

    if (this.cursor === "" || this.cursor === null) {
      this.cursor = "wait";
    }
    document.body.style.cursor = this.cursor;
  }
}

export class FullScreenMessage {
  private fullScreenMessageContainer: HTMLElement = document.getElementById("fullScreenMessage");
  private fullScreenMessageText: HTMLElement = document.getElementById("fullScreenMessageText");

  public showMessage = (text: string): void => {
    if (this.fullScreenMessageContainer && this.fullScreenMessageText) {
      this.fullScreenMessageContainer.setAttribute("style", "display: block;");
      this.fullScreenMessageText.innerHTML = text + this.getReloadButton();
    }
  };

  private getReloadButton(buttonText: string = "Načíst znovu"): string {
    return '<div class="cleaner height20"></div><button onClick="location.reload(true);">' + buttonText + "</button>";
  }
}

export const K2Key = "K2NejlepsiERP";
